"use client";
import FilterAndSort, {FilterAndSortProps} from "@c/newsMedia/allNews/filter-and-sort";
import { StaticImageData } from "next/image";
import SectionWrapper from "@cc/section-wrapper";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  alpha,
  Box,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { MaterialImage } from "@cc/material-components";
import { H6_1, H6_2 } from "@theme/components/typography.fontvariant";
import { useMemo, useState } from "react";
import { textEllipsis } from "@util/string-helpers";
import Link from "@cc/link";
import { format } from "date-fns/format";
import { getOrganizationListForForm } from "@/cms-helpers/api-helper.client";
import { toNumber, uniq } from "lodash";
import useSWR from "swr";
import { I18nLocale } from "@/i18nConfig.server";

export interface NewsListingProps {
  data: {
    title: string;
    company: string;
    tag: string;
    image: string | StaticImageData;
    date: string;
    description: string;
    timeToRead: string;
    publication: string;
    href: string;
  }[];
  locale: I18nLocale;
}

const NewsListing = (props: NewsListingProps) => {
  const { data, locale } = props;
  const [view, setView] = useState<"grid" | "list">("grid");
  const isLg = useMediaQuery<Theme>((theme) => theme.breakpoints.up("lg"));
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );

  const { data: CompaniesData } = useSWR("contact-us-companies", () =>
    getOrganizationListForForm(locale),
  );

  const organizationList = useMemo(() => {
    if (!CompaniesData) return [];
    return uniq(
      CompaniesData.map((item) => {
        return item.title;
      }),
    );
  }, [CompaniesData]);

  const topicList = ["Press-Release", "News", "Blogs"];

  const yearList = useMemo(() => {
    return uniq(
      data
        .map((item) => {
          return format(new Date(item.date), "yyyy");
        })
        .filter((value) => !!value),
    );
  }, [data]);

  yearList.sort((a, b) => toNumber(b) - toNumber(a));

  return (
    <>
      <FilterAndSort
        view={view}
        setView={setView}
        organizations={organizationList}
        topics={topicList}
        years={yearList}
      />
      <SectionWrapper color={"primary.main"}>
        <Grid2 container columnSpacing={3} rowSpacing={6}>
          {data.map((item, index) => {
            const {
              title,
              company,
              tag,
              image,
              date,
              description,
              timeToRead,
              publication,
              href,
            } = item;
            const isList = view === "list" && isLg;
            return (
              <Grid2
                key={`${title}-${index}`}
                xs={12}
                sm={isList ? 12 : 6}
                lg={isList ? 12 : 4}
              >
                <Link href={href} style={{ textDecoration: "none" }}>
                  <Stack
                    sx={{
                      cursor: "pointer",
                      position: "relative",
                      rowGap: 2,
                      columnGap: 3,
                      "&:hover": {
                        ".MuiBox-root:after": {
                          opacity: 1,
                        },
                      },
                    }}
                    direction={isList ? "row" : "column"}
                    alignItems={isList ? "center" : "flex-start"}
                  >
                    <Box
                      width={isList ? 300 : 1}
                      maxHeight={{ xs: "none", md: 418.66 }}
                      position={"relative"}
                      sx={{
                        "&:after": {
                          display: isMobile ? "none" : "flex",
                          opacity: 0,
                          content: `"View News"`,
                          fontSize: H6_2,
                          color: "white",
                          width: isList ? 300 : 1,
                          height: 1,
                          position: "absolute",
                          top: 0,
                          left: 0,
                          background:
                            "linear-gradient(0deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.70) 100%)",
                          alignItems: "center",
                          justifyContent: "center",
                          textAlign: "center",
                          transition: "all .3s",
                        },
                      }}
                    >
                      <MaterialImage
                        src={image}
                        alt={title}
                        width={isList ? 300 : 420}
                        height={isList ? 300 : 420}
                        sx={{
                          width: isList ? 300 : 1,
                          aspectRatio: "1/1",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                    {company && (
                      <Typography
                        sx={{
                          color: (theme) => theme.palette.secondary.main,
                          bgcolor: (theme) =>
                            alpha(theme.palette.common.black, 0.6),
                          p: 1.5,
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      >
                        {company}
                      </Typography>
                    )}
                    <Stack width={isList ? "calc(100% - 316px)" : 1} rowGap={1}>
                      <Stack
                        direction={
                          isList && tag
                            ? "row-reverse"
                            : isList
                              ? "row"
                              : "column"
                        }
                        justifyContent={isList ? "space-between" : "flex-start"}
                        rowGap={1}
                      >
                        {tag && (
                          <Typography
                            variant={"caption"}
                            sx={{
                              color: "grey.600",
                              p: 0.75,
                              bgcolor: isList ? "#EAEAEA" : "#fff",
                              width: "fit-content",
                            }}
                          >
                            {tag}
                          </Typography>
                        )}
                        <Typography
                          fontSize={H6_1}
                          textTransform={"capitalize"}
                        >
                          {title}
                        </Typography>
                      </Stack>
                      <Typography variant={"body2"}>
                        {`${publication ? publication : ""} ${
                          date ? `| ${date}` : ""
                        } ${timeToRead ? `| ${timeToRead}` : ""}`}
                      </Typography>
                      {isList && (
                        <Typography
                          variant={"body1"}
                          sx={{
                            ...textEllipsis(2),
                          }}
                        >
                          {description}
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </Link>
              </Grid2>
            );
          })}
        </Grid2>
      </SectionWrapper>
    </>
  );
};

export default NewsListing;
